import { useEffect, useId, useState } from "react";
import { CityCombobox } from "@/components/address/city-combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useWholesaleContext, useGeneratePortalPassword, type PortalPassword } from "@/hooks/use-wholesale";
import { parseNumberInput } from "@/lib/wholesale-format";

export type PartnerFormValues = {
  type: "company" | "person";
  name: string;
  companyIdNumber: string;
  vatNumber: string;
  customerCode: string;
  contactName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  discountPct: number;
  paymentTermDays: number;
  note: string;
  storeDiscounts: { storeId: string; discountPct: number }[];
};

function initialState(initial?: Partial<PartnerFormValues>) {
  return {
    type: (initial?.type ?? "company") as "company" | "person",
    name: initial?.name ?? "",
    companyIdNumber: initial?.companyIdNumber ?? "",
    vatNumber: initial?.vatNumber ?? "",
    customerCode: initial?.customerCode ?? "",
    contactName: initial?.contactName ?? "",
    phone: initial?.phone ?? "",
    email: initial?.email ?? "",
    address: initial?.address ?? "",
    city: initial?.city ?? "",
    postalCode: initial?.postalCode ?? "",
    country: initial?.country ?? "BA",
    discountPct: String(initial?.discountPct ?? 0),
    paymentTermDays: String(initial?.paymentTermDays ?? 0),
    note: initial?.note ?? "",
  };
}

function initialStoreDiscounts(initial?: Partial<PartnerFormValues>): Record<string, string> {
  return Object.fromEntries((initial?.storeDiscounts ?? []).map((d) => [d.storeId, String(d.discountPct)]));
}

const Field = ({ id, label, children }: { id: string; label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <Label htmlFor={id} className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export function PartnerForm({
  initial,
  onSubmit,
  submitting = false,
  submitLabel = "Sačuvaj",
  resetKey,
  partnerId,
}: {
  initial?: Partial<PartnerFormValues>;
  onSubmit: (values: PartnerFormValues) => void;
  submitting?: boolean;
  submitLabel?: string;
  /** Promjena vrijednosti ponovo puni formu iz `initial` — npr. nakon uspješnog spremanja,
   *  da se vide vrijednosti koje je server normalizovao ili ograničio. */
  resetKey?: string | number;
  /** Postoji samo za već spremljenog partnera — dok se ne sačuva, nema koga pozvati u portal. */
  partnerId?: string;
}) {
  const uid = useId();
  const fid = (k: string) => `${uid}-${k}`;
  const [form, setForm] = useState(() => initialState(initial));
  const [storeDiscounts, setStoreDiscounts] = useState<Record<string, string>>(() => initialStoreDiscounts(initial));
  const ctx = useWholesaleContext();
  const sources = ctx.data?.sources ?? [];
  const generatePassword = useGeneratePortalPassword();
  const [sendEmail, setSendEmail] = useState(true);
  // Lozinka živi samo u ovom stanju komponente — nigdje se ne kešira i nestaje zatvaranjem forme.
  const [result, setResult] = useState<PortalPassword | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  function handleGenerate() {
    // Čuva i od dvostrukog klika prije nego se dugme stigne onemogućiti.
    if (!partnerId || generatePassword.isPending) return;
    setResult(null);
    setError(null);
    setCopyError(null);
    generatePassword.mutate(
      { id: partnerId, sendEmail },
      {
        onSuccess: setResult,
        onError: (e: any) => setError(e?.message || "Greška pri generisanju lozinke"),
      },
    );
  }

  async function handleCopy() {
    if (!result) return;
    // Clipboard API ne postoji na nesigurnim (http) porijeklima — neuspjeh kopiranja ne
    // smije srušiti panel ni maknuti lozinku s ekrana, ona ostaje vidljiva kao izvor istine.
    try {
      if (!navigator.clipboard) throw new Error("nedostupno");
      await navigator.clipboard.writeText(result.password);
      setCopyError(null);
    } catch {
      setCopyError("Kopiranje nije uspjelo — prepišite lozinku ručno.");
    }
  }

  useEffect(() => {
    if (resetKey === undefined) return;
    setForm(initialState(initial));
    setStoreDiscounts(initialStoreDiscounts(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const set = (k: keyof ReturnType<typeof initialState>, v: string) => setForm((f) => ({ ...f, [k]: v }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      type: form.type,
      name: form.name.trim(),
      companyIdNumber: form.type === "company" ? form.companyIdNumber.trim() : "",
      vatNumber: form.type === "company" ? form.vatNumber.trim() : "",
      customerCode: form.customerCode.trim(),
      contactName: form.type === "company" ? form.contactName.trim() : "",
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      postalCode: form.postalCode.trim(),
      country: form.country.trim() || "BA",
      discountPct: parseNumberInput(form.discountPct) ?? 0,
      paymentTermDays: Math.max(0, Math.round(parseNumberInput(form.paymentTermDays) ?? 0)),
      note: form.note.trim(),
      storeDiscounts: Object.entries(storeDiscounts).flatMap(([storeId, v]) => {
        const n = parseNumberInput(v);
        return n === null ? [] : [{ storeId, discountPct: n }];
      }),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <RadioGroup
        value={form.type}
        onValueChange={(v) => set("type", v)}
        className="flex items-center gap-4"
      >
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="company" id={fid("type-company")} />
          <Label htmlFor={fid("type-company")} className="text-xs cursor-pointer">Firma</Label>
        </div>
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="person" id={fid("type-person")} />
          <Label htmlFor={fid("type-person")} className="text-xs cursor-pointer">Fizičko lice</Label>
        </div>
      </RadioGroup>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field id={fid("name")} label={form.type === "company" ? "Naziv firme" : "Ime i prezime"}>
          <Input id={fid("name")} className="h-8 text-xs" value={form.name} onChange={(e) => set("name", e.target.value)} required />
        </Field>
        {form.type === "company" && (
          <Field id={fid("contact")} label="Kontakt osoba">
            <Input id={fid("contact")} className="h-8 text-xs" value={form.contactName} onChange={(e) => set("contactName", e.target.value)} />
          </Field>
        )}
        {form.type === "company" && (
          <Field id={fid("company-id")} label="ID broj">
            <Input id={fid("company-id")} className="h-8 text-xs" value={form.companyIdNumber} onChange={(e) => set("companyIdNumber", e.target.value)} />
          </Field>
        )}
        {form.type === "company" && (
          <Field id={fid("vat")} label="PDV broj">
            <Input id={fid("vat")} className="h-8 text-xs" value={form.vatNumber} onChange={(e) => set("vatNumber", e.target.value)} />
          </Field>
        )}
        {/* Šifra kupca vrijedi i za fizička lica — to je oznaka partnera, ne porezni podatak. */}
        <Field id={fid("customer-code")} label="Šifra kupca">
          <Input id={fid("customer-code")} className="h-8 text-xs" value={form.customerCode} onChange={(e) => set("customerCode", e.target.value)} />
        </Field>
        <Field id={fid("email")} label="Email">
          <Input id={fid("email")} type="email" className="h-8 text-xs" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field id={fid("phone")} label="Telefon">
          <Input id={fid("phone")} className="h-8 text-xs" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field id={fid("address")} label="Adresa">
          <Input id={fid("address")} className="h-8 text-xs" value={form.address} onChange={(e) => set("address", e.target.value)} />
        </Field>
        <Field id={fid("city")} label="Grad">
          <CityCombobox
            id={fid("city")}
            city={form.city}
            postalCode={form.postalCode}
            onSelect={(city, postalCode) => setForm((f) => ({ ...f, city, postalCode }))}
          />
        </Field>
        <Field id={fid("postal")} label="Poštanski broj">
          <Input id={fid("postal")} className="h-8 text-xs" value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
        </Field>
        <Field id={fid("country")} label="Država">
          <Input id={fid("country")} className="h-8 text-xs" value={form.country} onChange={(e) => set("country", e.target.value)} />
        </Field>
        <Field id={fid("discount")} label="Rabat (%)">
          <Input
            id={fid("discount")}
            type="number" min={0} max={100} step="0.01"
            className="h-8 text-xs tabular-nums"
            value={form.discountPct}
            onChange={(e) => set("discountPct", e.target.value)}
          />
        </Field>
        <Field id={fid("term")} label="Rok plaćanja (dana)">
          <Input
            id={fid("term")}
            type="number" min={0} step={1}
            className="h-8 text-xs tabular-nums"
            value={form.paymentTermDays}
            onChange={(e) => set("paymentTermDays", e.target.value)}
          />
        </Field>
      </div>

      <Field id={fid("note")} label="Napomena">
        <Textarea id={fid("note")} className="text-xs min-h-16" value={form.note} onChange={(e) => set("note", e.target.value)} />
      </Field>

      {sources.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Rabat po shopu</p>
          <p className="text-xs text-muted-foreground">Prazno polje znači da se koristi globalni rabat partnera.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sources.map((s) => (
              <div key={s.id} className="flex items-center gap-2">
                <Label htmlFor={fid(`store-${s.id}`)} className="text-xs flex-1 truncate font-normal">{s.name}</Label>
                <Input
                  id={fid(`store-${s.id}`)}
                  type="number" min={0} max={100} step="0.01"
                  className="h-8 text-xs w-24 tabular-nums"
                  placeholder="—"
                  value={storeDiscounts[s.id] ?? ""}
                  onChange={(e) => setStoreDiscounts((d) => ({ ...d, [s.id]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {partnerId && (
        <div className="flex flex-col gap-2 border-t pt-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Button type="button" variant="outline" size="sm" disabled={generatePassword.isPending} onClick={handleGenerate}>
              {generatePassword.isPending ? "Generisanje…" : "Generiši lozinku za portal"}
            </Button>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              Pošalji i mailom
            </label>
            {error && <span className="text-xs text-destructive">{error}</span>}
          </div>
          {result && (
            <div className="rounded border bg-muted/40 p-3 text-xs space-y-1">
              <div>Email: <strong>{result.email}</strong></div>
              <div className="flex items-center gap-2">
                Lozinka: <strong className="font-mono tracking-wider">{result.password}</strong>
                <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
                  Kopiraj
                </Button>
              </div>
              {copyError && <div className="text-destructive">{copyError}</div>}
              <div className="text-muted-foreground">
                {result.emailSent
                  ? "Lozinka je poslana i mailom."
                  : `Mail nije poslan${result.emailError ? ` (${result.emailError})` : ""} — predajte lozinku partneru lično.`}
              </div>
              <div className="text-muted-foreground">Lozinka se prikazuje samo sada — zatvaranjem forme nestaje.</div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? "Spremanje…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
